import { useCallback, useEffect, useState } from 'react';
import { MessageSquare, Send } from 'lucide-react';
import ListaEstandar from '../ui/ListaEstandar';
import ChipEstado from '../ui/ChipEstado';
import EstadoVacio from '../ui/EstadoVacio';
import TiempoRelativo from '../ui/TiempoRelativo';
import { SelectFiltro } from '../ui/Filtros';
import { ESTADO_INTERESADO } from '../ui/estados';
import { apiPanel } from './api';
import { useCapa } from '../ui/navegacionReact';
import CortesiaDelBot from '../integraciones/CortesiaDelBot';
import ConocimientoDelBot from '../integraciones/ConocimientoDelBot';
import PromptDelAsistente from '../asistente/PromptDelAsistente';

/* =====================================================================
   INTERESADOS · el CRM mínimo de TEGO · 30 de agosto de 2026
   =====================================================================
   Todo lo que entra por el bot de Telegram acaba aquí. Es la única
   pantalla donde se ve quién preguntó por TEGO y qué se hizo con él.

   LO QUE ESTA PANTALLA NO HACE, Y ES DELIBERADO:

     · No borra. Que alguien preguntó el martes es un hecho. La base ni
       siquiera le concede DELETE al panel, así que no es una omisión de
       la interfaz: es una regla.
     · No contesta por el chat. Se contesta en Telegram, con la persona
       delante. Un botón de «responder» aquí obligaría a arrastrar todo
       el hilo y sería un segundo sitio donde mirar lo mismo.

   LO PRIMERO QUE SE VE ES LO QUE HAY QUE HACER HOY: la pestaña arranca
   en «Sin contestar», porque un interesado nuevo que se queda sin
   respuesta deja de ser un interesado.
   ===================================================================== */

/* LOS CINCO ESTADOS, EN ORDEN DE EMBUDO Y EN CRISTIANO.
   Eran cuatro hasta el 1 de septiembre de 2026 (migración 200). Las
   etiquetas dicen lo que le pasó a la PERSONA, no lo que hicimos con la
   fila: «Sin contestar» reclama algo, «Contestado» no.

   «Ya le pasé precios» es el que faltaba y el que más se nota: sin él,
   quien ya recibió números y está esperando se ve igual que quien solo
   recibió un saludo — y son las dos situaciones más distintas que hay
   en esta lista. */
const ESTADOS = [
  { valor: 'nuevo', etiqueta: 'Sin contestar' },
  { valor: 'contactado', etiqueta: 'Ya le escribí' },
  { valor: 'cotizado', etiqueta: 'Ya le pasé precios' },
  { valor: 'ganado', etiqueta: 'Ganado' },
  { valor: 'perdido', etiqueta: 'Se perdió' },
];

// El único que pide algo más antes de moverlo. La base lo exige desde la
// migración 200; aquí se pregunta ANTES para no mandar una petición que
// ya se sabe que va a volver con un error.
const ESTADO_QUE_PIDE_MOTIVO = 'perdido';

// De dónde vino, dicho en cristiano. La clave es el `?start=` del botón
// que pulsó en el sitio, y sin esta traducción la columna diría «planta»
// y habría que saberse los parámetros de memoria.
const ORIGEN = {
  cotizar: 'Pidió cotización',
  plan: 'Miró los planes',
  control: 'Quiere TEGO Control',
  piloto: 'Quiere ser piloto',
  planta: 'Quiere TEGO Planta',
  hola: 'Escribió por el botón de contacto',
  alta: 'Va a abrir su cuenta',
  directo: 'Escribió al bot directamente',
};

// Lo que contestó al guion, con el nombre que se le enseñó a él.
const PREGUNTA = {
  tipo: 'Qué controla',
  municipio: 'Municipio',
  tamano: 'Cuántos son',
  quien: 'Quién es',
  planta: 'La planta',
  duele: 'Qué pierden hoy',
  estaciones: 'Estaciones',
  terminales: 'Terminales',
  empresa: 'Empresa',
  factura: 'Factura',
};

export default function Interesados() {
  const [filas, setFilas] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [estado, setEstado] = useState('nuevo');
  const [abierto, setAbierto] = useState(null);
  useCapa(!!abierto, () => setAbierto(null));      // la hoja del interesado: atrás la cierra

  const cargar = useCallback(() => {
    const p = new URLSearchParams();
    if (estado) p.set('estado', estado);
    setCargando(true);
    Promise.all([apiPanel.get(`/interesados?${p}`), apiPanel.get('/interesados/resumen')])
      .then(([lista, r]) => {
        setFilas(lista);
        setResumen(r);
        setError(null);
      })
      .catch((e) => setError(e.message))
      .finally(() => setCargando(false));
  }, [estado]);

  // LA CARGA SE DIFIERE UN TIC, igual que en Clientes.jsx. Llamarla
  // dentro del efecto dispara el `setState` en el mismo render y eso son
  // renders en cascada — el lint lo marca y tiene razón.
  useEffect(() => {
    const t = setTimeout(cargar, 0);
    return () => clearTimeout(t);
  }, [cargar]);

  async function mover(fila, nuevo, motivo) {
    try {
      await apiPanel.post(`/interesados/${fila.id}/estado`, {
        estado: nuevo,
        // Solo viaja cuando hace falta. Mandar `motivo_perdida` en todos
        // los cambios sería mandar un dato que el servidor va a tirar.
        ...(nuevo === ESTADO_QUE_PIDE_MOTIVO ? { motivo_perdida: motivo } : {}),
      });
      cargar();
      setAbierto(null);
    } catch (e) {
      setError(e.message);
    }
  }

  const columnas = [
    {
      etiqueta: 'Quién',
      valor: (f) => f.nombre || f.usuario || `chat ${f.chat_id}`,
      render: (f) => (
        <div>
          <div className="font-semibold text-gray-900">
            {f.nombre || 'Sin nombre'}
          </div>
          <div className="text-xs text-gray-500">
            {f.usuario ? `@${f.usuario}` : `chat ${f.chat_id}`}
          </div>
        </div>
      ),
    },
    {
      etiqueta: 'Qué quiere',
      valor: (f) => ORIGEN[f.origen] || f.origen,
      render: (f) => (
        <div>
          <div>{ORIGEN[f.origen] || f.origen}</div>
          {f.veces > 1 && (
            // VOLVER A ESCRIBIR ES LA SEÑAL MÁS FUERTE QUE HAY. Quien
            // abre el bot cuatro veces por lo mismo está esperando una
            // respuesta que no llega.
            <div className="text-xs text-amber-700 font-medium">
              Ha vuelto {f.veces} veces
            </div>
          )}
        </div>
      ),
    },
    {
      etiqueta: 'Estado',
      valor: (f) => f.estado,
      render: (f) => <ChipEstado mapa={ESTADO_INTERESADO} clave={f.estado} />,
    },
    {
      etiqueta: 'Quién lo atiende',
      valor: (f) => f.atendido_por || '',
      render: (f) =>
        f.atendido_por ? (
          <span className="text-sm text-gray-700">{f.atendido_por}</span>
        ) : (
          <span className="text-sm text-gray-400">Nadie todavía</span>
        ),
    },
    {
      etiqueta: 'Última vez',
      valor: (f) => f.ultimo_en,
      render: (f) => <TiempoRelativo fecha={f.ultimo_en} />,
    },
  ];

  const acciones = (f) => [{ etiqueta: 'Ver y mover', onClick: () => setAbierto(f) }];

  return (
    <>
      <ListaEstandar
        titulo="Interesados"
        subtitulo="Todo el que ha escrito al bot de TEGO en Telegram, y qué se hizo con él."
        filtros={
          <SelectFiltro
            valor={estado}
            onChange={setEstado}
            opciones={ESTADOS.map((e) => ({
              valor: e.valor,
              etiqueta:
                resumen ? `${e.etiqueta} (${resumen.por_estado[e.valor] ?? 0})` : e.etiqueta,
            }))}
            vacio="Todos"
            etiqueta="Estado del interesado"
          />
        }
        columnas={columnas}
        filas={filas}
        cargando={cargando}
        acciones={acciones}
        vacio={
          <EstadoVacio
            icono={MessageSquare}
            titulo={
              estado === 'nuevo'
                ? 'No hay nadie esperando respuesta'
                : 'Aquí no hay nadie todavía'
            }
            descripcion={
              estado === 'nuevo'
                ? 'Cuando alguien escriba al bot desde el sitio, aparecerá aquí y te llegará un aviso a Telegram.'
                : 'Cambia el filtro de arriba para ver los de otro estado.'
            }
          />
        }
      />

      {error && (
        <div className="mt-4 rounded-lg bg-red-50 text-red-800 text-sm p-3">{error}</div>
      )}

      {/* Cómo saluda el bot (16 sep 2026): los textos de cortesía, aquí
          porque es donde el dueño ya mira a su bot. */}
      <CortesiaDelBot />
      {/* Lo que el bot sabe decir (BOT-INTELIGENTE F5): la base de conocimiento y lo que faltó. */}
      <ConocimientoDelBot />
      {/* El prompt del asistente operativo (ASISTENTE-OPERATIVO AO5): un dato, editable aquí. */}
      <PromptDelAsistente />

      {abierto && (
        <Ficha
          interesado={abierto}
          onCerrar={() => setAbierto(null)}
          onMover={mover}
        />
      )}
    </>
  );
}

/* La ficha. Se abre encima y no en otra página: casi siempre se mira
   para hacer una cosa —contestar y mover el estado— y volver. */
function Ficha({ interesado, onCerrar, onMover }) {
  const respuestas = Object.entries(interesado.respuestas || {});
  // PREGUNTAR ANTES, NO REGAÑAR DESPUES. «Se perdió» no dispara la
  // petición: abre el campo del motivo aquí mismo, con el botón de
  // confirmar apagado y DICIENDO POR QUE está apagado. Mandarlo y
  // enseñar el error del servidor haría lo mismo con un viaje de más y
  // un regaño de más.
  const [pidiendoMotivo, setPidiendoMotivo] = useState(false);
  const [motivo, setMotivo] = useState('');
  const motivoVale = motivo.trim().length > 0;

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-6 z-50"
      onClick={onCerrar}
    >
      <div
        className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b">
          <div className="font-bold text-lg text-gray-900">
            {interesado.nombre || 'Sin nombre'}
          </div>
          <div className="text-sm text-gray-500">
            {ORIGEN[interesado.origen] || interesado.origen}
            {interesado.usuario ? ` · @${interesado.usuario}` : ''}
          </div>
        </div>

        <div className="p-5 space-y-4">
          {respuestas.length > 0 ? (
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase mb-2">
                Lo que contestó
              </div>
              <dl className="space-y-1.5">
                {respuestas.map(([clave, valor]) => (
                  <div key={clave} className="flex gap-2 text-sm">
                    <dt className="text-gray-500 w-36 shrink-0">
                      {PREGUNTA[clave] || clave}
                    </dt>
                    <dd className="text-gray-900 font-medium">{String(valor)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ) : (
            /* NO SE DEJA EN BLANCO. Un interesado sin respuestas es el que
               abrió el bot y no terminó el guion, y eso hay que decirlo:
               es distinto de un error de carga. */
            <p className="text-sm text-gray-500">
              Abrió el bot pero no llegó a contestar las preguntas. Escríbele:
              lo que quería lo dice de dónde vino.
            </p>
          )}

          {/* CÓMO SE LE CONTESTA, dicho aquí y no en un manual. Es la
              pregunta que se hace todo el que abre esta ficha. */}
          <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900 flex gap-2">
            <Send className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              Para contestarle, abre el chat del bot en Telegram y búscalo por{' '}
              {interesado.usuario ? (
                <b>@{interesado.usuario}</b>
              ) : (
                <>su número de chat <b>{interesado.chat_id}</b></>
              )}
              . Lo que escribas ahí le llega directo.
            </span>
          </div>

          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase mb-2">
              Mover a
            </div>
            <div className="flex flex-wrap gap-2">
              {ESTADOS.filter((e) => e.valor !== interesado.estado).map((e) => (
                <button
                  key={e.valor}
                  onClick={() =>
                    e.valor === ESTADO_QUE_PIDE_MOTIVO
                      ? setPidiendoMotivo(true)
                      : onMover(interesado, e.valor)
                  }
                  className="px-3 py-2 rounded-lg border text-sm font-medium hover:bg-gray-50"
                >
                  {e.etiqueta}
                </button>
              ))}
            </div>

            {pidiendoMotivo && (
              <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
                <label
                  htmlFor="motivo-perdida"
                  className="block text-sm font-semibold text-gray-900"
                >
                  ¿Qué pasó?
                </label>
                {/* LA AYUDA VA AQUI Y NO EN UN MANUAL. Sin el ejemplo,
                    lo que se escribe es «no» o «nada», y entonces la
                    columna existe y sigue sin enseñar nada. */}
                <p className="text-xs text-gray-600 mt-1 mb-2">
                  Una línea con lo que pasó: «lo vio caro», «no contestó en
                  dos semanas», «se fue con otro». Es lo único que después
                  permite saber qué se está perdiendo y por qué.
                </p>
                <input
                  id="motivo-perdida"
                  type="text"
                  autoFocus
                  maxLength={200}
                  value={motivo}
                  onChange={(ev) => setMotivo(ev.target.value)}
                  placeholder="No contestó en dos semanas"
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm"
                />
                <div className="flex gap-2 mt-2">
                  <button
                    disabled={!motivoVale}
                    onClick={() =>
                      onMover(interesado, ESTADO_QUE_PIDE_MOTIVO, motivo.trim())
                    }
                    className="px-3 py-2 rounded-lg bg-amber-600 text-white text-sm font-medium disabled:bg-gray-200 disabled:text-gray-500"
                  >
                    Marcar como perdido
                  </button>
                  <button
                    onClick={() => {
                      setPidiendoMotivo(false);
                      setMotivo('');
                    }}
                    className="px-3 py-2 rounded-lg border text-sm font-medium"
                  >
                    Mejor no
                  </button>
                </div>
                {/* EL BOTON APAGADO DICE POR QUE LO ESTA. Un botón gris y
                    mudo se lee como «esta pantalla está rota». */}
                {!motivoVale && (
                  <p className="text-xs text-gray-600 mt-2">
                    Escribe el motivo para poder marcarlo.
                  </p>
                )}
              </div>
            )}

            <p className="text-xs text-gray-500 mt-2">
              Al moverlo queda apuntado que fuiste tú, con la fecha. Nada se
              borra nunca.
            </p>
          </div>
        </div>

        <div className="p-4 border-t">
          <button
            onClick={onCerrar}
            className="w-full py-2.5 rounded-lg bg-gray-100 font-medium text-gray-700"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
